<template>
	<div class="container">
		<transition mode="out-in" name="fade">
			<div class="transition-root" key="connecting" v-if="connecting">
				<div class="row">
					<loading-indicator>{{$t('room.connecting')}}..</loading-indicator>
				</div>
			</div>
			<div class="transition-root" key="loading" v-if="loading">
				<div class="row">
					<loading-indicator>{{$t('room.loading')}}..</loading-indicator>
				</div>
			</div>

			<div class="transition-root" key="notConnecting" v-if="!connecting && !loading">
				<img alt="logo" class="roskilde-logo" src="~@img/misc/roskilde_logo.png">
				<img class="logo mt-4" src="~@img/misc/lineal_logo.png">
				<h6 class="">Roskildemodellen</h6>
				<h1 class="mb-3">{{$t('room.roomCode')}}</h1>
				<emoji-code-picker
					type="tiny"
					v-bind:activeEmojiIndex="activeEmojiIndex"
					v-model="roomCode"
					v-on:activeEmojiIndexChange="activeEmojiIndexChange"
					v-on:fillOutStatusChanged="onFilloutStatusChanged"
				/>
				<app-button :enabled="canJoin" v-on:click.native="onClickJoin">{{$t('misc.start')}}</app-button>
			</div>
		</transition>
	</div>
</template>

<script>
	import {$eBus} from '@/event-bus'

	import PlayerType from '@/enum/PlayerType'
	import GameClientEvent from '@/enum/GameClientEvent'
	import LoadingIndicator from '@/components/loading-indicator/index'
	import * as MutationTypes from '@/store/mutation-types'
	import EmojiCodePicker from '@/components/emoji-code/picker'
	import AppButton from '@/components/app-button'
	import {CurrentUrlInfo} from '@/current-url-info'
	import AppConfig from '@/app-config'
	import {mapGetters} from 'vuex'
	import {getStudentUrl} from '@/utils/UrlUtil'
	import LanguageSelector from '@/components/language-selector';

	export default {
		components: {
			LoadingIndicator,
			EmojiCodePicker,
			AppButton,
			LanguageSelector
		},
		data() {
			return {
				canJoin: false,
				connecting: false,
				reconnecting: false,
				loading: false,
				roomCode: '0000',
				activeEmojiIndex: 0
			}
		},
		computed: {
			...mapGetters({
				lastEmojiCode: 'getLastEmojiCode'
			}),
		},
		beforeDestroy() {
			$eBus.$off(GameClientEvent.Error, this.onClientError)
			$eBus.$off(GameClientEvent.Open, this.onClientCreated)
			$eBus.$off(GameClientEvent.RoomJoin, this.onRoomJoin)
		},
		mounted() {
			if (this.$gameClient && this.$gameClient.inRoom()) {
				this.$gameClient.leaveRoom();
			}

			if (this.lastEmojiCode) {
				this.$apiClient.isRoomJoinable(this.lastEmojiCode, () => {
					this.tryReconnect();
				}, () => {
					this.$store.commit(MutationTypes.SET_LAST_EMOJI_CODE, null);
				});
			}

			this.$store.dispatch({type: 'fullReset'});
		},
		methods: {
			goHome() {
				this.$router.push('/');
			},
			tryReconnect() {
				if (this.reconnecting) {
					return;
				}

				this.reconnecting = true;
				this.roomCode = this.lastEmojiCode;
				this.activeEmojiIndex = this.roomCode.length - 1;
				this.canJoin = true;
				this.onClickJoin();
			},
			activeEmojiIndexChange(activeEmojiIndex) {
				this.activeEmojiIndex = activeEmojiIndex
			},
			onFilloutStatusChanged(canJoin) {
				this.canJoin = canJoin
			},
			onClickJoin() {
				if (!this.canJoin) {
					return
				}

				this.connect()
			},
			onClientError(client) {
				if (client) {
					client.close()
				}

				this.$store.commit(MutationTypes.SET_ERROR_ICON, 'connectionError')
				this.$store.commit(MutationTypes.SET_ERROR_MESSAGE, this.$t('room.connectionError'))
			},
			onClientCreated(client) {
				this.$gameClient.joinRoom(this.roomCode)
			},
			onRoomJoin() {
				$eBus.$off(GameClientEvent.Error, this.onClientError)
				$eBus.$off(GameClientEvent.Open, this.onClientCreated)

				this.reconnecting = false
				this.connecting = false

				this.$gameClient.send({
					playerType: PlayerType.Student
				})

				this.$store.commit(MutationTypes.SET_LAST_EMOJI_CODE, this.roomCode);
				this.$router.push(getStudentUrl('lobby'))
			},
			connect() {
				$eBus.$on(GameClientEvent.Error, this.onClientError)
				$eBus.$on(GameClientEvent.Open, this.onClientCreated)
				$eBus.$on(GameClientEvent.RoomJoin, this.onRoomJoin)

				let currentUrlInfo = CurrentUrlInfo.instance()

				let host = AppConfig.colyseusHost || currentUrlInfo.getCurrentHost()
				let port = AppConfig.colyseusPort
				let wsProtocol = AppConfig.colyseusSecure ? 'wss' : 'ws';

				this.$apiClient.onGameClientEndPointChanged(host + ':' + port)

				this.connecting = true

				this.$apiClient.isRoomJoinable(this.roomCode, () => {
					this.$gameClient.connect(host, port, wsProtocol)

					this.loading = false
				}, (message) => {
					this.roomCode = '0000';
					this.activeEmojiIndex = 0;

					if (!this.reconnecting) { //Dont show errors on reconnect as it was forced by the app
						let status = this.$apiClient.lastResponse ? this.$apiClient.lastResponse.status : null;

						if (status == 404) {
							this.$store.commit(MutationTypes.SET_ERROR_ICON, 'emojiCode')
							this.$store.commit(MutationTypes.SET_ERROR_MESSAGE, this.$t('room.codeError'))
						} else {
							this.$store.commit(MutationTypes.SET_ERROR_ICON, 'connectionError')
							this.$store.commit(MutationTypes.SET_ERROR_MESSAGE, this.$t('room.serverError'))
						}
					}

					this.reconnecting = false
					this.connecting = false
				})
			}
		}
	}
</script>

<style lang="less" scoped>
	.roskilde-logo {
		position: absolute;
		top: 5px;
		left: 10px;
		width: 30vw;
		max-width: 250px;
	}

	.logo {
		width: 150px;
		height: auto;
	}
</style>
